import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * The engine throws deliberately — a quietly wrong shopping list is worse than
 * one that stops. But without a boundary React turns any throw into a blank
 * white screen, and a blank screen in a supermarket helps nobody. This keeps
 * the loud failure and gives it somewhere to land.
 *
 * `resetKey` exists because a caught error otherwise outlives the screen that
 * threw it: React unmounts the children and renders this boom card instead,
 * and nothing about that state ever clears itself — so a crash on the List
 * screen went on being rendered for every OTHER tab too, Settings included,
 * the one screen that could actually undo whatever caused it. The app passes
 * the current view as `resetKey`; changing tabs is what proves someone has
 * moved past the screen that broke, so that is what is allowed to clear it.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: unknown },
  { message: string | null }
> {
  override state: { message: string | null } = { message: null };

  static getDerivedStateFromError(error: unknown): { message: string } {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Eden Eats hit an error', error, info.componentStack);
  }

  override componentDidUpdate(prevProps: { resetKey?: unknown }): void {
    if (this.state.message !== null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ message: null });
    }
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;
    return (
      <section className="boom">
        <h1>Something went wrong</h1>
        <p className="boom-detail">{this.state.message}</p>
        <p className="boom-detail">
          If this started after changing nursery days, open Settings.
        </p>
        <button type="button" className="primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </section>
    );
  }
}
